import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { RiskFormValues } from '../schema.ts';
import { RISK_TYPE_OPTIONS } from '../constants.ts';
import { classificationOptions } from '../../../utils/controlConstants.js';
import type { OptionLabel } from '../../../types/options.ts';
import { Select } from '../../../components/common';
import type { MenuStaticActionConfig } from '../../../components/common/SelectMenuStaticAction.tsx';
import {
  useControlItgcDomainOptions,
} from '../../../hooks/controls/useControlItgcDomainOptions.ts';
import { useRiskControlFilterOptions, useRiskProcessFilterOptions } from '../../../hooks/risks/useRisksTableList.ts';
import { PaginatedSelectMenuList } from './PaginatedSelectMenuList.tsx';
import { CreateItgcDomainModal } from './CreateItgcDomainModal.tsx';
import { RiskAssociationsEditor, type ProcessCardContext } from './RiskAssociationsEditor.tsx';
import type { CreateItgcDomainResult } from '../../../request/controls/controlItgcOptions.ts';

const ITGC_DOMAIN_SELECT_STYLES = {
  control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
    ...base,
    minHeight: 42,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
    fontSize: '0.875rem',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
    },
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 40,
    padding: '2px 12px',
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    color: '#6b7280',
    padding: '0 8px',
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    maxHeight: 280,
    overflowY: 'auto',
  }),
};

const ITGC_DOMAIN_SELECT_COMPONENTS = {
  MenuList: PaginatedSelectMenuList,
};

const EMPTY_ASSOCIATION_OPTIONS: OptionLabel[] = [];
const RISK_LEVEL_OPTIONS: Array<{ value: RiskFormValues['riskLevel']; label: string; activeClassName: string }> = [
  { value: 'High', label: 'High', activeClassName: 'border-red-600 bg-red-50 text-red-700' },
  { value: 'Medium', label: 'Medium', activeClassName: 'border-amber-500 bg-amber-50 text-amber-700' },
  { value: 'Low', label: 'Low', activeClassName: 'border-green-600 bg-green-50 text-green-700' },
];

interface RiskFieldShellProps {
  id?: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}

const RiskFieldShell = ({ id, label, required, error, hint, children }: RiskFieldShellProps) => (
  <div>
    <label htmlFor={id} className="block font-medium mb-2 text-sm text-gray-600">
      {label}
      {required ? <span className="text-red-500 ml-0.5">*</span> : null}
    </label>
    {children}
    {hint && !error ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
    {error ? (
      <p className="text-sm text-red-600 mt-1" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

const fieldBorderClassName = (error?: string) =>
  error ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500';

interface RiskTextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
}

const RiskTextInput = React.forwardRef<HTMLInputElement, RiskTextInputProps>(
  ({ id, label, error, required, hint, ...rest }, ref) => (
    <RiskFieldShell id={id} label={label} error={error} required={required} hint={hint}>
      <input
        ref={ref}
        id={id}
        className={`w-full rounded-lg border bg-gray-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 ${fieldBorderClassName(error)}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
    </RiskFieldShell>
  )
);
RiskTextInput.displayName = 'RiskTextInput';

interface RiskTextAreaInputProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
}

const RiskTextAreaInput = React.forwardRef<HTMLTextAreaElement, RiskTextAreaInputProps>(
  ({ id, label, error, required, hint, rows = 3, ...rest }, ref) => (
    <RiskFieldShell id={id} label={label} error={error} required={required} hint={hint}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        className={`w-full rounded-lg border bg-gray-50 px-3 py-2 text-sm focus:ring-2 focus:border-transparent resize-y disabled:opacity-50 disabled:bg-gray-50 ${fieldBorderClassName(error)}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
    </RiskFieldShell>
  )
);
RiskTextAreaInput.displayName = 'RiskTextAreaInput';

interface RiskSelectInputProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label: string;
  options: Array<{ value: string | number; label: string }>;
  error?: string;
  required?: boolean;
  hint?: string;
  placeholder?: string;
}

const RiskSelectInput = React.forwardRef<HTMLSelectElement, RiskSelectInputProps>(
  ({ id, label, options, error, required, hint, placeholder, ...rest }, ref) => (
    <RiskFieldShell id={id} label={label} error={error} required={required} hint={hint}>
      <select
        ref={ref}
        id={id}
        className={`w-full rounded-lg border bg-gray-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 ${fieldBorderClassName(error)}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </RiskFieldShell>
  )
);
RiskSelectInput.displayName = 'RiskSelectInput';

interface RiskLevelControlProps {
  id: string;
  label: string;
  required?: boolean;
  value: RiskFormValues['riskLevel'];
  onChange: (value: RiskFormValues['riskLevel']) => void;
  error?: string;
}

const RiskLevelControl = ({ id, label, required, value, onChange, error }: RiskLevelControlProps) => (
  <RiskFieldShell id={`${id}-${value.toLowerCase()}`} label={label} required={required} error={error}>
    <div className="flex items-center gap-2" role="group" aria-label={label}>
      {RISK_LEVEL_OPTIONS.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            id={`${id}-${option.value.toLowerCase()}`}
            type="button"
            onClick={() => onChange(option.value)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${
              isSelected ? option.activeClassName : 'border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            aria-pressed={isSelected}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </RiskFieldShell>
);

interface RiskReadonlyFieldProps {
  label: string;
  children: React.ReactNode;
}

const RiskReadonlyField = ({ label, children }: RiskReadonlyFieldProps) => (
  <div>
    <p className="block font-medium mb-2 text-sm text-gray-600">{label}</p>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

interface RiskFormFieldsProps {
  register: UseFormRegister<RiskFormValues>;
  control: Control<RiskFormValues>;
  errors: FieldErrors<RiskFormValues>;
  setValue?: UseFormSetValue<RiskFormValues>;
  clearErrors?: UseFormClearErrors<RiskFormValues>;
  initialProcessOptions?: OptionLabel[];
  initialControlOptions?: OptionLabel[];
  /** UUID-keyed display / navigation context from the loaded risk (edit mode). */
  processContextByUuid?: Record<string, ProcessCardContext>;
  /** When true, render only the inputs without the surrounding card wrapper and section heading. */
  hideCard?: boolean;
  detailsHeaderSlot?: React.ReactNode;
  /** Read-only meta values surfaced inside Risk Overview in edit mode. */
  riskSystemId?: string;
  sourceLabel?: string;
  updatedLabel?: string;
  readOnly?: boolean;
  currentValues?: Partial<RiskFormValues>;
  headerActions?: React.ReactNode;
  /** Controls dropdown classification filter (from risk details `classification`). */
  controlClassification?: string;
  /** Create-page only: show editable Classification + Domain. */
  showClassificationFields?: boolean;
  /** Detail/edit: optional domain label from GET risk `itgc_domain`. */
  itgcDomainLabel?: string;
}

export const RiskFormFields: React.FC<RiskFormFieldsProps> = ({
  register,
  control,
  errors,
  setValue,
  clearErrors,
  initialProcessOptions = EMPTY_ASSOCIATION_OPTIONS,
  initialControlOptions = EMPTY_ASSOCIATION_OPTIONS,
  processContextByUuid,
  hideCard = false,
  detailsHeaderSlot,
  riskSystemId,
  sourceLabel,
  updatedLabel,
  readOnly = false,
  currentValues,
  headerActions,
  controlClassification = 'business',
  showClassificationFields = false,
  itgcDomainLabel = '',
}) => {
  const { fields, append, remove } = useFieldArray({ control, name: 'processAssociations' });
  const watchedClassification = useWatch({ control, name: 'classification' });
  const watchedRiskDescription = useWatch({ control, name: 'riskDescription' });
  const watchedRiskCategory = useWatch({ control, name: 'riskCategory' });
  const watchedRiskLevel = useWatch({ control, name: 'riskLevel' });
  const aiRecommendRiskContext = useMemo(
    () => ({
      riskName: String(riskSystemId || '').trim(),
      riskDescription: String(watchedRiskDescription || currentValues?.riskDescription || '').trim(),
      riskCategory: String(watchedRiskCategory || currentValues?.riskCategory || '').trim(),
      riskLevel: String(watchedRiskLevel || currentValues?.riskLevel || '').trim(),
    }),
    [
      currentValues?.riskCategory,
      currentValues?.riskDescription,
      currentValues?.riskLevel,
      riskSystemId,
      watchedRiskCategory,
      watchedRiskDescription,
      watchedRiskLevel,
    ]
  );
  const effectiveClassification = String(
    showClassificationFields
      ? watchedClassification
      : currentValues?.classification ?? watchedClassification ?? ''
  )
    .trim()
    .toLowerCase();
  const isITGCClassification = effectiveClassification === 'itgc';
  const showCreateItgcFields = showClassificationFields && isITGCClassification;
  const showDetailItgcLabels = !showClassificationFields && isITGCClassification;
  const showDetailItgcDomainEdit = showDetailItgcLabels && !readOnly;
  const classificationLabel =
    classificationOptions.find((o) => o.value === effectiveClassification)?.label ??
    (effectiveClassification ? effectiveClassification.toUpperCase() : '');
  const [domainSearchInput, setDomainSearchInput] = useState('');
  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [selectedDomainOption, setSelectedDomainOption] = useState<
    { value: string; label: string; code?: string } | null
  >(null);
  const [extraDomainOptions, setExtraDomainOptions] = useState<
    Array<{ value: string; label: string; code?: string }>
  >([]);
  const selectedDomainId = useWatch({ control, name: 'itgcDomainId' });
  const normalizedSelectedDomainId =
    typeof selectedDomainId === 'string' ? selectedDomainId.trim() : '';
  const queryClient = useQueryClient();
  const domainOptionsQuery = useControlItgcDomainOptions(
    domainSearchInput,
    showCreateItgcFields || showDetailItgcDomainEdit
  );
  const domainOptions = useMemo(() => {
    const fromPages = (domainOptionsQuery.data?.pages ?? []).flatMap((page) => page.items);
    if (extraDomainOptions.length === 0) return fromPages;
    const seen = new Set(fromPages.map((o) => o.value));
    const extras = extraDomainOptions.filter((o) => !seen.has(o.value));
    return [...extras, ...fromPages];
  }, [domainOptionsQuery.data?.pages, extraDomainOptions]);

  const domainOptionsForSelect = useMemo(() => {
    // While searching, show only API search matches. Keep selected cached option only when
    // search is empty so the closed/idle field can still resolve and display the label.
    if (domainSearchInput.trim()) return domainOptions;
    const merged = new Map<string, { value: string; label: string; code?: string }>();
    if (normalizedSelectedDomainId && selectedDomainOption) {
      merged.set(selectedDomainOption.value, selectedDomainOption);
    }
    domainOptions.forEach((option) => merged.set(option.value, option));
    return Array.from(merged.values());
  }, [domainOptions, domainSearchInput, normalizedSelectedDomainId, selectedDomainOption]);

  useEffect(() => {
    if (!normalizedSelectedDomainId) {
      setSelectedDomainOption(null);
      return;
    }
    const match = domainOptions.find((option) => option.value === normalizedSelectedDomainId);
    if (match) {
      setSelectedDomainOption((prev) =>
        prev?.value === match.value && prev.label === match.label ? prev : match
      );
      return;
    }
    if (itgcDomainLabel) {
      setSelectedDomainOption((prev) =>
        prev?.value === normalizedSelectedDomainId
          ? prev
          : { value: normalizedSelectedDomainId, label: itgcDomainLabel }
      );
    }
  }, [domainOptions, itgcDomainLabel, normalizedSelectedDomainId]);

  const handleDomainSelectChange = useCallback(
    (value: string | string[], onFieldChange: (nextValue: string) => void) => {
      const nextValue = typeof value === 'string' ? value : '';
      clearErrors?.('itgcDomainId');
      onFieldChange(nextValue);
      const fromSearch = domainOptions.find((option) => option.value === nextValue);
      setSelectedDomainOption(
        nextValue ? fromSearch ?? { value: nextValue, label: nextValue } : null
      );
      setDomainSearchInput('');
    },
    [clearErrors, domainOptions]
  );

  const handleDomainInputChange = useCallback((value: string, meta: { action: string }) => {
    if (meta.action === 'input-change') {
      setDomainSearchInput(value);
      return;
    }
    if (
      meta.action === 'set-value' ||
      meta.action === 'menu-close' ||
      meta.action === 'input-blur'
    ) {
      setDomainSearchInput('');
    }
  }, []);

  const domainMenuStaticAction = useMemo<MenuStaticActionConfig>(
    () => ({
      label: 'Add domain',
      onClick: () => setShowAddDomainModal(true),
      icon: <Plus className="h-4 w-4 shrink-0" aria-hidden />,
      'aria-label': 'Add domain',
    }),
    []
  );

  const handleDomainCreated = useCallback(
    (created: CreateItgcDomainResult) => {
      const label =
        created.code && created.name
          ? `${created.code} - ${created.name}`
          : created.name || created.code || created.id;
      setExtraDomainOptions((prev) => {
        if (prev.some((o) => o.value === created.id)) return prev;
        return [{ value: created.id, label, code: created.code || undefined }, ...prev];
      });
      setSelectedDomainOption({ value: created.id, label, code: created.code || undefined });
      setValue?.('itgcDomainId', created.id, { shouldDirty: true, shouldValidate: true });
      clearErrors?.('itgcDomainId');
      void queryClient.invalidateQueries({ queryKey: ['controls', 'itgc-domain-options'] });
    },
    [clearErrors, queryClient, setValue]
  );

  useEffect(() => {
    if (!showClassificationFields || !setValue) return;
    if (isITGCClassification) {
      setValue('processAssociations', [], { shouldDirty: true, shouldValidate: true });
      return;
    }
    setValue('itgcDomainId', '', { shouldDirty: true, shouldValidate: true });
    setDomainSearchInput('');
    setSelectedDomainOption(null);
  }, [isITGCClassification, setValue, showClassificationFields]);

  const [processSearch, setProcessSearch] = useState('');
  const [processSearchInput, setProcessSearchInput] = useState('');
  const [controlSearch, setControlSearch] = useState('');
  const [controlSearchInput, setControlSearchInput] = useState('');
  useEffect(() => {
    const nextSearch = processSearchInput.trim();
    if (nextSearch === '') {
      setProcessSearch('');
      return;
    }
    const t = window.setTimeout(() => {
      setProcessSearch(nextSearch);
    }, 400);
    return () => window.clearTimeout(t);
  }, [processSearchInput]);
  useEffect(() => {
    const nextSearch = controlSearchInput.trim();
    if (nextSearch === '') {
      setControlSearch('');
      return;
    }
    const t = window.setTimeout(() => {
      setControlSearch(nextSearch);
    }, 400);
    return () => window.clearTimeout(t);
  }, [controlSearchInput]);
  const fetchAssociationPickerOptions = !readOnly && !isITGCClassification;
  const {
    data: processPages,
    isFetching: loadingProcesses,
    isFetchingNextPage: loadingMoreProcesses,
    hasNextPage: hasMoreProcesses,
    fetchNextPage: fetchMoreProcesses,
  } = useRiskProcessFilterOptions(processSearch, '', fetchAssociationPickerOptions);
  const allProcesses = useMemo(
    () => (processPages?.pages ?? []).flatMap((page) => page.items),
    [processPages]
  );
  const {
    data: controlPages,
    isFetching: loadingControls,
    isFetchingNextPage: loadingMoreControls,
    hasNextPage: hasMoreControls,
    fetchNextPage: fetchMoreControls,
  } = useRiskControlFilterOptions(controlSearch, controlClassification, fetchAssociationPickerOptions);
  const allControls = useMemo(
    () => (controlPages?.pages ?? []).flatMap((p) => p.items),
    [controlPages]
  );

  const processOptions = useMemo(() => {
    const options: Array<OptionLabel & { cycleId: string; routeProcessId: string }> = [];
    const seen = new Set<string>();
    allProcesses.forEach((process: { value?: string; label?: string; cycleId?: string; routeProcessId?: string }) => {
      const processUuid = String(process?.value ?? '').trim();
      if (!processUuid || seen.has(processUuid)) return;
      seen.add(processUuid);
      options.push({
        value: processUuid,
        label: String(process?.label ?? processUuid),
        cycleId: String(process?.cycleId ?? '').trim(),
        routeProcessId: String(process?.routeProcessId ?? '').trim(),
      });
    });
    return options;
  }, [allProcesses]);

  const controlOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();
    allControls.forEach((control: { value?: string; label?: string }) => {
      const value = String(control?.value ?? '').trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({
        value,
        label: String(control?.label ?? value),
      });
    });
    return options;
  }, [allControls]);

  const processLabelCache = useMemo(() => {
    const acc = initialProcessOptions.reduce<Record<string, string>>((labels, option) => {
      if (option.value && option.label) labels[option.value] = option.label;
      return labels;
    }, {});

    if (!readOnly) {
      for (const option of processOptions) {
        if (option.value && option.label) {
          acc[option.value] = option.label;
        }
        if (option.routeProcessId && option.routeProcessId !== option.value) {
          acc[option.routeProcessId] = option.label;
        }
      }
    }

    return acc;
  }, [initialProcessOptions, processOptions, readOnly]);

  const controlLabelCache = useMemo(() => {
    const acc = initialControlOptions.reduce<Record<string, string>>((labels, option) => {
      if (option.value && option.label) labels[option.value] = option.label;
      return labels;
    }, {});

    if (!readOnly) {
      for (const option of controlOptions) {
        if (option.value && option.label) acc[option.value] = option.label;
      }
    }

    return acc;
  }, [controlOptions, initialControlOptions, readOnly]);

  const hasMetaRow = Boolean(riskSystemId || updatedLabel);
  const trimmedSource = (sourceLabel ?? '').trim();

  const detailsFields = readOnly ? (
    <>
      {hasMetaRow && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <RiskReadonlyField label="Risk ID">
            {riskSystemId || <span className="text-gray-400">-</span>}
          </RiskReadonlyField>
          <RiskReadonlyField label="Last Updated">
            {updatedLabel || <span className="text-gray-400">-</span>}
          </RiskReadonlyField>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <RiskReadonlyField label="Client Reference ID">
          {currentValues?.clientRiskId || <span className="text-gray-400">-</span>}
        </RiskReadonlyField>
        <RiskReadonlyField label="Source">
          {trimmedSource ? (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
              {trimmedSource}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </RiskReadonlyField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <RiskReadonlyField label="Risk Category">
          {currentValues?.riskCategory || <span className="text-gray-400">-</span>}
        </RiskReadonlyField>
        <RiskReadonlyField label="Risk level">
          {currentValues?.riskLevel || <span className="text-gray-400">-</span>}
        </RiskReadonlyField>
      </div>
      {showDetailItgcLabels ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <RiskReadonlyField label="Classification">
            {classificationLabel ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {classificationLabel}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </RiskReadonlyField>
          <RiskReadonlyField label="Domain">
            {itgcDomainLabel || <span className="text-gray-400">-</span>}
          </RiskReadonlyField>
        </div>
      ) : !showClassificationFields ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <RiskReadonlyField label="Classification">
            {classificationLabel ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {classificationLabel}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </RiskReadonlyField>
        </div>
      ) : null}
      <RiskReadonlyField label="Description">
        <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap break-words">
          {currentValues?.riskDescription || '-'}
        </p>
      </RiskReadonlyField>
    </>
  ) : (
    <>
      {hasMetaRow && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <RiskReadonlyField label="Risk ID">
            {riskSystemId || <span className="text-gray-400">-</span>}
          </RiskReadonlyField>
          <RiskReadonlyField label="Last Updated">
            {updatedLabel || <span className="text-gray-400">-</span>}
          </RiskReadonlyField>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <RiskTextInput
          id="clientRiskId"
          label="Client Reference ID"
          placeholder="Enter client reference ID"
          hint="Optional. If provided, use at least 3 characters (max 50)."
          error={errors.clientRiskId?.message}
          {...register('clientRiskId')}
        />
        <RiskReadonlyField label="Source">
          {trimmedSource ? (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
              {trimmedSource}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </RiskReadonlyField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <RiskSelectInput
          id="riskCategory"
          label="Risk Category"
          required
          options={RISK_TYPE_OPTIONS}
          error={errors.riskCategory?.message}
          {...register('riskCategory')}
        />
        <Controller
          control={control}
          name="riskLevel"
          render={({ field }) => (
            <RiskLevelControl
              id="riskLevel"
              label="Risk level"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.riskLevel?.message}
            />
          )}
        />
      </div>

      {showDetailItgcLabels ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <RiskReadonlyField label="Classification">
            {classificationLabel ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {classificationLabel}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </RiskReadonlyField>
          {showDetailItgcDomainEdit ? (
            <Controller
              control={control}
              name="itgcDomainId"
              render={({ field }) => {
                const domainError = errors.itgcDomainId?.message as string | undefined;
                const domainSelectProps = {
                  loadingMoreOptions: domainOptionsQuery.isFetchingNextPage,
                  hasMoreOptions: Boolean(domainOptionsQuery.hasNextPage),
                  onOptionsNearEnd: () => {
                    if (domainOptionsQuery.hasNextPage && !domainOptionsQuery.isFetchingNextPage) {
                      void domainOptionsQuery.fetchNextPage();
                    }
                  },
                };
                return (
                  <RiskFieldShell
                    id="itgcDomainId"
                    label="Domain"
                    required
                    error={domainError}
                  >
                    <Select
                      inputId="itgcDomainId"
                      aria-label="ITGC domain"
                      className={domainError ? 'ring-1 ring-red-500 rounded-lg' : 'w-full'}
                      options={domainOptionsForSelect}
                      value={field.value ?? ''}
                      onChange={(value) => handleDomainSelectChange(value, field.onChange)}
                      onBlur={field.onBlur}
                      isSearchable
                      isLoading={domainOptionsQuery.isLoading}
                      inputValue={domainSearchInput}
                      onInputChange={handleDomainInputChange}
                      filterOption={() => true}
                      components={ITGC_DOMAIN_SELECT_COMPONENTS}
                      styles={ITGC_DOMAIN_SELECT_STYLES}
                      menuStaticAction={domainMenuStaticAction}
                      {...domainSelectProps}
                    />
                  </RiskFieldShell>
                );
              }}
            />
          ) : (
            <RiskReadonlyField label="Domain">
              {itgcDomainLabel || <span className="text-gray-400">-</span>}
            </RiskReadonlyField>
          )}
        </div>
      ) : !showClassificationFields ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <RiskReadonlyField label="Classification">
            {classificationLabel ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {classificationLabel}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </RiskReadonlyField>
        </div>
      ) : null}

      {showClassificationFields ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <Controller
            control={control}
            name="classification"
            render={({ field }) => (
              <RiskSelectInput
                id="classification"
                label="Classification"
                required
                options={classificationOptions}
                error={errors.classification?.message}
                value={field.value ?? 'business'}
                onChange={(e) => {
                  clearErrors?.('classification');
                  field.onChange(e.target.value);
                }}
                onBlur={field.onBlur}
              />
            )}
          />
          {showCreateItgcFields ? (
            <Controller
              control={control}
              name="itgcDomainId"
              render={({ field }) => {
                const domainError = errors.itgcDomainId?.message as string | undefined;
                const domainSelectProps = {
                  loadingMoreOptions: domainOptionsQuery.isFetchingNextPage,
                  hasMoreOptions: Boolean(domainOptionsQuery.hasNextPage),
                  onOptionsNearEnd: () => {
                    if (domainOptionsQuery.hasNextPage && !domainOptionsQuery.isFetchingNextPage) {
                      void domainOptionsQuery.fetchNextPage();
                    }
                  },
                };
                return (
                  <RiskFieldShell
                    id="itgcDomainId"
                    label="Domain"
                    required
                    error={domainError}
                  >
                    <Select
                      inputId="itgcDomainId"
                      aria-label="ITGC domain"
                      className={domainError ? 'ring-1 ring-red-500 rounded-lg' : 'w-full'}
                      options={domainOptionsForSelect}
                      value={field.value ?? ''}
                      onChange={(value) => handleDomainSelectChange(value, field.onChange)}
                      onBlur={field.onBlur}
                      isSearchable
                      isLoading={domainOptionsQuery.isLoading}
                      inputValue={domainSearchInput}
                      onInputChange={handleDomainInputChange}
                      filterOption={() => true}
                      components={ITGC_DOMAIN_SELECT_COMPONENTS}
                      styles={ITGC_DOMAIN_SELECT_STYLES}
                      {...domainSelectProps}
                      menuStaticAction={domainMenuStaticAction}
                      placeholder="Select domain..."
                      placeholderOnFocus="Search domains..."
                      noOptionsMessage={() =>
                        domainOptionsQuery.isLoading ? 'Loading domains...' : 'No domains found'
                      }
                      menuPlacement="auto"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      menuPosition="absolute"
                    />
                  </RiskFieldShell>
                );
              }}
            />
          ) : null}
        </div>
      ) : null}

      <RiskTextAreaInput
        id="riskDescription"
        label="Description"
        required
        rows={5}
        placeholder="What can go wrong?"
        error={errors.riskDescription?.message}
        {...register('riskDescription')}
      />

    </>
  );

  const detailsCard = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Risk Overview</h2>
        {headerActions}
      </div>
      <div className="p-6 space-y-5">
        {detailsHeaderSlot ? <div className="space-y-2">{detailsHeaderSlot}</div> : null}
        {detailsFields}
      </div>
    </div>
  );

  const associationsFields = (
    <RiskAssociationsEditor
      bleedCardTitle={!hideCard}
      readOnly={readOnly}
      control={control}
      fields={fields}
      append={append}
      remove={remove}
      setValue={setValue}
      aiRecommendRiskContext={aiRecommendRiskContext}
      processOptions={processOptions}
      processLabelCache={processLabelCache}
      loadingProcesses={loadingProcesses || loadingMoreProcesses}
      loadingMoreOptions={loadingMoreProcesses}
      processSearchInput={processSearchInput}
      hasNextPage={Boolean(hasMoreProcesses)}
      onProcessSearchChange={setProcessSearchInput}
      onLoadMoreProcesses={() => {
        if (hasMoreProcesses && !loadingMoreProcesses) {
          void fetchMoreProcesses();
        }
      }}
      controlOptions={controlOptions}
      controlLabelCache={controlLabelCache}
      loadingControls={loadingControls || loadingMoreControls}
      loadingMoreControls={loadingMoreControls}
      controlSearchInput={controlSearchInput}
      hasMoreControls={Boolean(hasMoreControls)}
      onControlSearchChange={setControlSearchInput}
      onLoadMoreControls={() => {
        if (hasMoreControls && !loadingMoreControls) {
          void fetchMoreControls();
        }
      }}
      processContextByUuid={processContextByUuid}
    />
  );

  if (hideCard) {
    return (
      <>
        <div className="space-y-4">
          {detailsFields}
          {!isITGCClassification ? associationsFields : null}
        </div>
        <CreateItgcDomainModal
          isOpen={showAddDomainModal}
          onClose={() => setShowAddDomainModal(false)}
          onCreated={handleDomainCreated}
        />
      </>
    );
  }

  const associationsCard = !isITGCClassification ? (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {associationsFields}
    </div>
  ) : null;

  return (
    <>
      <div className="space-y-5">
        {detailsCard}
        {associationsCard}
      </div>
      <CreateItgcDomainModal
        isOpen={showAddDomainModal}
        onClose={() => setShowAddDomainModal(false)}
        onCreated={handleDomainCreated}
      />
    </>
  );
};

export const defaultRiskFormValues: RiskFormValues = {
  clientRiskId: '',
  riskDescription: '',
  riskCategory: 'SOX',
  riskLevel: 'Medium',
  classification: 'business',
  itgcDomainId: '',
  processAssociations: [],
};
